'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useInView } from 'framer-motion';

interface TextScrambleProps {
  text: string;
  duration?: number;
  autostart?: boolean;
  className?: string;
  delay?: number;
}

const chars = '!<>-_\\/[]{}—=+*^?#________';

export const TextScramble: React.FC<TextScrambleProps> = ({
  text,
  duration = 1.5,
  autostart = true,
  className = '',
  delay = 0,
}) => {
  const [displayText, setDisplayText] = useState(text);
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-10%" });

  const scramble = useCallback(() => {
    let frame = 0;
    const totalFrames = duration * 60; // Assuming 60fps
    const queue: Array<{ from: string; to: string; start: number; end: number; char?: string }> = [];

    for (let i = 0; i < text.length; i++) {
      const from = text[i];
      const to = text[i];
      const start = Math.floor(Math.random() * totalFrames / 2);
      const end = start + Math.floor(Math.random() * totalFrames / 2);
      queue.push({ from, to, start, end });
    }

    let requestId: number;

    const update = () => {
      let output = '';
      let complete = 0;

      for (let i = 0, n = queue.length; i < n; i++) {
        let { from, to, start, end, char } = queue[i];
        if (frame >= end) {
          complete++;
          output += to;
        } else if (frame >= start) {
          if (!char || Math.random() < 0.28) {
            char = chars[Math.floor(Math.random() * chars.length)];
            queue[i].char = char;
          }
          output += `<span class="opacity-50">${char}</span>`;
        } else {
          output += from;
        }
      }

      setDisplayText(output);

      if (complete === queue.length) {
        // Animation finished
      } else {
        frame++;
        requestId = requestAnimationFrame(update);
      }
    };

    update();

    return () => cancelAnimationFrame(requestId);
  }, [text, duration]);

  useEffect(() => {
    if (autostart && isInView && !hasAnimated) {
      const timer = setTimeout(() => {
        scramble();
        setHasAnimated(true);
      }, delay * 1000);
      return () => clearTimeout(timer);
    }
  }, [autostart, isInView, hasAnimated, scramble, delay]);

  return (
    <span 
      ref={containerRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: displayText }}
    />
  );
};
